import { FC, FormEvent, useEffect, useState } from 'react'
import { LuMail } from 'react-icons/lu'
import { useSearchParams } from 'react-router-dom'
import { contentApi } from '@/api'
import { getSeoContent } from '@/content/siteContent'
import { useI18n } from '@/hooks'
import { usePageMetadata } from '@/seo/usePageMetadata'
import type { AboutPageContent, AboutSectionId } from '@/types/content'

const aboutSectionIds: AboutSectionId[] = ['story', 'product', 'contact', 'faq']

const isAboutSectionId = (value: string | null): value is AboutSectionId =>
  value !== null && aboutSectionIds.includes(value as AboutSectionId)

const AboutSectionTabs = ({
  content,
  activeSectionId,
  onSelectSection,
}: {
  content: AboutPageContent
  activeSectionId: AboutSectionId
  onSelectSection: (sectionId: AboutSectionId) => void
}) => {
  return (
    <nav className="border-y border-neutral-200 py-5" aria-label={content.menuLabel}>
      <div className="flex justify-end">
        <div className="grid gap-0 sm:grid-cols-2 lg:flex lg:items-center lg:gap-9">
          {content.sections.map((section, index) => {
            const isActive = section.id === activeSectionId

            return (
              <button
                key={section.id}
                type="button"
                onClick={() => onSelectSection(section.id)}
                className={`group relative flex items-baseline gap-3 border-0 bg-transparent px-0 py-3 text-left transition lg:py-2 ${isActive ? 'text-neutral-950' : 'text-neutral-400 hover:text-neutral-900'}`}
              >
                <span className="text-xs tabular-nums tracking-[0.18em]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-base font-medium tracking-tight">
                  {section.label}
                </span>
                <span
                  className={`absolute bottom-0 left-0 h-px bg-neutral-950 transition-all duration-300 ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`}
                />
              </button>
            )
          })}
        </div>
      </div>
    </nav>
  )
}

const StoryPanel = ({ story }: Pick<AboutPageContent, 'story'>) => (
  <section className="grid gap-10 lg:grid-cols-[420px_minmax(0,1fr)] lg:gap-16">
    <div className="overflow-hidden bg-neutral-100">
      <img src={story.imageUrl} alt={story.imageAlt} className="h-full min-h-[460px] w-full object-cover" />
    </div>
    <div className="self-center">
      <p className="text-xs uppercase tracking-[0.24em] text-neutral-400">Brand Story</p>
      <h1 className="mt-4 text-[clamp(2.7rem,5vw,4.2rem)] font-medium leading-none tracking-tight text-neutral-950">
        {story.title}
      </h1>
      <div className="mt-8 max-w-2xl space-y-5 text-[1.04rem] leading-[1.95] text-neutral-500">
        {story.paragraphs.map(paragraph => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      <p className="mt-10 border-t border-neutral-200 pt-6 font-serif text-4xl italic text-neutral-900">{story.signature}</p>
    </div>
  </section>
)

const ProductPanel = ({ product }: Pick<AboutPageContent, 'product'>) => (
  <section>
    <div className="space-y-14">
      {product.sections.map((section, index) => (
        <article
          key={section.id}
          className="grid gap-8 border-y border-neutral-200 py-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1fr)] lg:gap-12"
        >
          <div className={`overflow-hidden bg-neutral-100 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
            <img src={section.imageUrl} alt={section.imageAlt} className="h-full min-h-[340px] w-full object-cover" />
          </div>
          <div className="self-center px-0 py-2 lg:px-6">
            <p className="text-xs uppercase tracking-[0.22em] text-neutral-400">{String(index + 1).padStart(2, '0')}</p>
            <h2 className="mt-3 text-[clamp(2rem,3vw,2.9rem)] font-medium leading-tight tracking-tight text-neutral-950">{section.title}</h2>
            <div className="mt-7 space-y-8 text-[1.03rem] leading-[1.9] text-neutral-500">
              {section.blocks.map(block => (
                <div key={block.title}>
                  <h3 className="text-[1.55rem] font-medium tracking-tight text-neutral-950">{block.title}</h3>
                  <div className="mt-4 space-y-4">
                    {block.paragraphs.map(paragraph => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </article>
      ))}
    </div>
  </section>
)

const FloatingTextField = ({
  label,
  type = 'text',
  value,
  onChange,
  required,
}: {
  label: string
  type?: string
  value: string
  onChange: (v: string) => void
  required?: boolean
}) => {
  const id = label.toLowerCase().replace(/\s+/g, '-')
  return (
    <>
      <div className="contact-input-container">
        <input
          id={id}
          type={type}
          required={required}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
        <label htmlFor={id} className="contact-label">{label}</label>
        <div className="contact-underline" />
      </div>
      <style>{`
        .contact-input-container {
          position: relative;
          margin: 12px 0;
          width: 100%;
        }
        .contact-input-container input {
          font-size: 16px;
          width: 100%;
          border: none;
          border-bottom: 2px solid #e5e5e5;
          padding: 8px 0;
          background-color: transparent;
          outline: none;
          color: #171717;
        }
        .contact-label {
          position: absolute;
          top: 0;
          left: 0;
          color: #b8b8b8;
          transition: all 0.3s ease;
          pointer-events: none;
          font-size: 16px;
        }
        .contact-input-container input:focus ~ .contact-label,
        .contact-input-container input:valid ~ .contact-label {
          top: -20px;
          font-size: 13px;
          color: #6b7280;
        }
        .contact-underline {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 2px;
          width: 100%;
          background-color: #a0a0a0;
          transform: scaleX(0);
          transition: all 0.3s ease;
        }
        .contact-input-container input:focus ~ .contact-underline,
        .contact-input-container input:valid ~ .contact-underline {
          transform: scaleX(1);
        }
      `}</style>
    </>
  )
}

const FloatingTextarea = ({
  label,
  value,
  onChange,
  required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  required?: boolean
}) => {
  const id = label.toLowerCase().replace(/\s+/g, '-')
  return (
    <>
      <div className="contact-input-container">
        <textarea
          id={id}
          required={required}
          rows={6}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ resize: 'none', fontSize: 16, width: '100%', border: 'none', borderBottom: '2px solid #e5e5e5', padding: '8px 0', background: 'transparent', outline: 'none', color: '#171717', display: 'block' }}
        />
        <label
          htmlFor={id}
          className="contact-label"
          style={{ top: value.length > 0 ? -20 : 0, fontSize: value.length > 0 ? 13 : 16, color: value.length > 0 ? '#6b7280' : '#b8b8b8' }}
        >
          {label}
        </label>
        <div
          className="contact-underline"
          style={{ transform: value.length > 0 ? 'scaleX(1)' : 'scaleX(0)' }}
        />
      </div>
    </>
  )
}

const ContactPanel = ({ contact }: Pick<AboutPageContent, 'contact'>) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSent(true)
  }

  return (
    <section>
      <div className="grid gap-16 lg:grid-cols-[5fr_7fr] lg:gap-24">

        {/* Left column – info */}
        <div>
          <span className="text-xs uppercase tracking-[0.22em] text-neutral-400">Get in touch</span>
          <h1 className="mt-5 text-[clamp(2.4rem,4vw,3.8rem)] font-medium leading-[1.05] tracking-tight text-neutral-950">
            {contact.title}
          </h1>
          <div className="mt-8 h-px w-16 bg-neutral-200" />
          <p className="mt-8 text-[1.03rem] leading-[1.9] text-neutral-500">
            {contact.intro}
          </p>

          <div className="mt-12 space-y-5">
            <a
              href={`mailto:${contact.email}`}
              className="group flex items-center gap-4 text-neutral-600 transition-colors duration-200 hover:text-neutral-950"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-neutral-200 text-neutral-400 transition-all duration-200 group-hover:border-neutral-950 group-hover:bg-neutral-950 group-hover:text-white">
                <LuMail size={16} />
              </span>
              <span className="text-sm tracking-wide">{contact.email}</span>
            </a>
          </div>
        </div>

        {/* Right column – form */}
        <div>
          {sent ? (
            <div className="flex min-h-[360px] flex-col items-start justify-center">
              <div className="flex h-14 w-14 items-center justify-center border border-neutral-950 text-neutral-950">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <polyline points="4,11 9,16 18,6" />
                </svg>
              </div>
              <h2 className="mt-6 text-3xl font-medium tracking-tight text-neutral-950">Message sent.</h2>
              <p className="mt-3 text-neutral-500">We'll get back to you as soon as possible.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-10">
              <FloatingTextField
                label={contact.form.namePlaceholder}
                value={name}
                onChange={setName}
                required
              />
              <FloatingTextField
                label={contact.form.emailPlaceholder}
                type="email"
                value={email}
                onChange={setEmail}
                required
              />
              <FloatingTextarea
                label={contact.form.messagePlaceholder}
                value={message}
                onChange={setMessage}
                required
              />
              <button
                type="submit"
                className="contact-send-btn"
              >
                <style>{`
                  .contact-send-btn {
                    font-family: inherit;
                    font-size: 17px;
                    background: #171717;
                    color: #f5f0e8;
                    padding: 0.65em 1.1em;
                    padding-left: 0.9em;
                    display: flex;
                    align-items: center;
                    border: none;
                    border-radius: 14px;
                    overflow: hidden;
                    transition: all 0.2s;
                    cursor: pointer;
                    letter-spacing: 0.03em;
                  }
                  .contact-send-btn:hover {
                    background: #2d2d2d;
                  }
                  .contact-send-btn span {
                    display: block;
                    margin-left: 0.35em;
                    transition: all 0.3s ease-in-out;
                  }
                  .contact-send-btn svg {
                    display: block;
                    transform-origin: center center;
                    transition: transform 0.3s ease-in-out;
                  }
                  .contact-send-btn:hover .svg-wrapper {
                    animation: cs-fly 0.6s ease-in-out infinite alternate;
                  }
                  .contact-send-btn:hover svg {
                    transform: translateX(1.2em) rotate(45deg) scale(1.1);
                  }
                  .contact-send-btn:hover span {
                    transform: translateX(5em);
                  }
                  .contact-send-btn:active {
                    transform: scale(0.95);
                  }
                  @keyframes cs-fly {
                    from { transform: translateY(0.1em); }
                    to   { transform: translateY(-0.1em); }
                  }
                `}</style>
                <div className="svg-wrapper-1">
                  <div className="svg-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22">
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path fill="currentColor" d="M1.946 9.315c-.522-.174-.527-.455.01-.634l19.087-6.362c.529-.176.832.12.684.638l-5.454 19.086c-.15.529-.455.547-.679.045L12 14l6-8-8 6-8.054-2.685z" />
                    </svg>
                  </div>
                </div>
                <span>{contact.form.submitLabel}</span>
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}

const FaqPanel = ({ faq }: Pick<AboutPageContent, 'faq'>) => (
  <section>
    <h1 className="text-[clamp(2.5rem,4vw,3.5rem)] font-semibold tracking-tight text-neutral-950">
      {faq.title}
    </h1>
    <div className="mt-10 space-y-12">
      {faq.groups.map(group => (
        <section key={group.title}>
          <h2 className="text-[2rem] font-semibold tracking-tight text-neutral-950">{group.title}</h2>
          <div className="mt-6 space-y-5">
            {group.items.map(item => (
              <article key={item.question} className="border-t border-neutral-200 pt-5">
                <h3 className="text-xl font-semibold leading-8 text-neutral-950">{item.question}</h3>
                <div className="mt-3 space-y-3 text-[1.03rem] leading-[1.9] text-neutral-500">
                  {item.answers.map(answer => (
                    <p key={answer}>{answer}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  </section>
)

const AboutPage: FC = () => {
  const { lang, fontClass, locale } = useI18n()
  const [content, setContent] = useState<AboutPageContent | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeSectionId, setActiveSectionId] = useState<AboutSectionId>('story')
  const seo = content?.seo ?? getSeoContent(lang).about

  useEffect(() => {
    let cancelled = false

    const loadAboutContent = async () => {
      const response = await contentApi.getAboutPage(lang)

      if (!cancelled) {
        setContent(response)
      }
    }

    void loadAboutContent()

    return () => {
      cancelled = true
    }
  }, [lang])

  useEffect(() => {
    if (!content) {
      return
    }

    const requestedSection = searchParams.get('section')
    if (isAboutSectionId(requestedSection) && content.sections.some(section => section.id === requestedSection)) {
      setActiveSectionId(requestedSection)
      return
    }

    setActiveSectionId(content.sections[0]?.id ?? 'story')
  }, [content, searchParams])

  const handleSelectSection = (sectionId: AboutSectionId) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set('section', sectionId)
    setActiveSectionId(sectionId)
    setSearchParams(nextSearchParams, { replace: true })
  }

  const renderActivePanel = () => {
    if (!content) {
      return null
    }

    if (activeSectionId === 'product') {
      return <ProductPanel product={content.product} />
    }

    if (activeSectionId === 'contact') {
      return <ContactPanel contact={content.contact} />
    }

    if (activeSectionId === 'faq') {
      return <FaqPanel faq={content.faq} />
    }

    return <StoryPanel story={content.story} />
  }

  usePageMetadata({
    title: seo.title,
    description: seo.description,
    path: '/about',
    locale,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: seo.title,
      description: seo.description,
      url: `${window.location.origin}/about`,
    },
  })

  return (
    <main className={`min-h-screen bg-white pt-14 text-neutral-900 ${fontClass}`}>
      <div className="mx-auto max-w-[1180px] px-5 py-14 md:px-8 lg:px-10 lg:py-20">
        {content ? (
          <AboutSectionTabs
            content={content}
            activeSectionId={activeSectionId}
            onSelectSection={handleSelectSection}
          />
        ) : null}

        <div className="mt-12 min-h-[520px]">
          {renderActivePanel()}
        </div>
      </div>

      {!content ? (
        <div className="mx-auto max-w-[1180px] px-5 pb-16 md:px-8 lg:px-10">
          <p className="border border-dashed border-neutral-300 px-6 py-4 text-sm text-neutral-500">
            Loading about content...
          </p>
        </div>
      ) : null}
    </main>
  )
}

export default AboutPage
